
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { BookOpen, FileText, Video, Download } from 'lucide-react';

const Resources = () => {
  const { t, getLocalizedPath } = useLanguage();

  return (
    <>
      <Helmet>
        <title>Resources for Mental Health Professionals | Global Therapy Hub</title>
        <meta name="description" content="Access professional resources, training materials, and tools for mental health professionals in our global network." />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-purple-50 to-blue-100">
          <div className="container mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Professional Resources
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Access comprehensive training materials, professional development resources, 
              and tools to enhance your therapy practice.
            </p>
          </div>
        </section>

        {/* Resources Grid */}
        <section className="py-16 px-4">
          <div className="container mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Training Materials */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <BookOpen className="h-12 w-12 text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Training Materials</h3>
                <p className="text-gray-600 mb-4">
                  Access Dr. José Luis Marín's complete Brief Psychotherapy training curriculum and materials.
                </p>
                <Button variant="outline" asChild>
                  <Link to="https://formacionpsicoterapia.com" target="_blank">
                    Access Materials
                  </Link>
                </Button>
              </div>

              {/* Clinical Guidelines */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <FileText className="h-12 w-12 text-green-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Clinical Guidelines</h3>
                <p className="text-gray-600 mb-4">
                  Evidence-based clinical guidelines and best practices for various therapeutic approaches.
                </p>
                <Button variant="outline" disabled>
                  Coming Soon
                </Button>
              </div>

              {/* Video Tutorials */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <Video className="h-12 w-12 text-purple-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Video Tutorials</h3>
                <p className="text-gray-600 mb-4">
                  Watch instructional videos on therapy techniques, platform usage, and professional development.
                </p>
                <Button variant="outline" disabled>
                  Coming Soon
                </Button>
              </div>

              {/* Research Papers */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <Download className="h-12 w-12 text-red-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Research Papers</h3>
                <p className="text-gray-600 mb-4">
                  Download the latest research papers on psychotherapy, mental health, and therapeutic outcomes.
                </p>
                <Button variant="outline" disabled>
                  Coming Soon
                </Button>
              </div>

              {/* Professional Tools */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <BookOpen className="h-12 w-12 text-indigo-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Professional Tools</h3>
                <p className="text-gray-600 mb-4">
                  Access assessment tools, therapy worksheets, and other professional resources.
                </p>
                <Button variant="outline" disabled>
                  Coming Soon
                </Button>
              </div>

              {/* Continuing Education */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <BookOpen className="h-12 w-12 text-orange-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Continuing Education</h3>
                <p className="text-gray-600 mb-4">
                  Find continuing education opportunities and professional development courses.
                </p>
                <Button variant="outline" disabled>
                  Coming Soon
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Resource */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl font-bold mb-8">Featured Resource</h2>
            <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold mb-4">Master in Brief Psychotherapy</h3>
              <p className="text-lg text-gray-600 mb-6">
                Dr. José Luis Marín's comprehensive training program that has certified over 30,000 
                mental health professionals worldwide. This program is a requirement for joining 
                our therapist network.
              </p>
              <Button size="lg" asChild>
                <Link to="https://formacionpsicoterapia.com" target="_blank">
                  Learn More About Training
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Resources;
